import { ChangeRequestDomain } from "./enums/ChangeRequestDomain";
import { ChangeRequestOperationType } from "./enums/ChangeRequestOperationType";
import { ChangeRequestStatus } from "./enums/ChangeRequestStatus";
import { Permissions } from "./enums/Permissions";

export { ChangeRequestDomain };

export type ChangeRequestOperationKind = "create" | "update" | "delete";

export const CHANGE_REQUEST_OPERATION_DOMAIN: Record<
  ChangeRequestOperationType,
  ChangeRequestDomain
> = {
  [ChangeRequestOperationType.ProductCreate]: ChangeRequestDomain.PRODUCT,
  [ChangeRequestOperationType.ProductUpdate]: ChangeRequestDomain.PRODUCT,
  [ChangeRequestOperationType.ProductDelete]: ChangeRequestDomain.PRODUCT,
  [ChangeRequestOperationType.GroupCreate]: ChangeRequestDomain.GROUP,
  [ChangeRequestOperationType.GroupUpdate]: ChangeRequestDomain.GROUP,
  [ChangeRequestOperationType.GroupDelete]: ChangeRequestDomain.GROUP,
  [ChangeRequestOperationType.ModifierCreate]: ChangeRequestDomain.MODIFIER,
  [ChangeRequestOperationType.ModifierUpdate]: ChangeRequestDomain.MODIFIER,
  [ChangeRequestOperationType.ModifierDelete]: ChangeRequestDomain.MODIFIER,
  [ChangeRequestOperationType.ModifierGroupCreate]:
    ChangeRequestDomain.MODIFIER_GROUP,
  [ChangeRequestOperationType.ModifierGroupUpdate]:
    ChangeRequestDomain.MODIFIER_GROUP,
  [ChangeRequestOperationType.ModifierGroupDelete]:
    ChangeRequestDomain.MODIFIER_GROUP,
  [ChangeRequestOperationType.UnitCreate]: ChangeRequestDomain.UNIT,
  [ChangeRequestOperationType.UnitUpdate]: ChangeRequestDomain.UNIT,
  [ChangeRequestOperationType.UnitDelete]: ChangeRequestDomain.UNIT,
  [ChangeRequestOperationType.PrepStationCreate]:
    ChangeRequestDomain.PREP_STATION,
  [ChangeRequestOperationType.PrepStationUpdate]:
    ChangeRequestDomain.PREP_STATION,
  [ChangeRequestOperationType.PrepStationDelete]:
    ChangeRequestDomain.PREP_STATION,
  [ChangeRequestOperationType.BookkeepingAccountCreate]:
    ChangeRequestDomain.BOOKKEEPING_ACCOUNT,
  [ChangeRequestOperationType.BookkeepingAccountUpdate]:
    ChangeRequestDomain.BOOKKEEPING_ACCOUNT,
  [ChangeRequestOperationType.BookkeepingAccountDelete]:
    ChangeRequestDomain.BOOKKEEPING_ACCOUNT,
  [ChangeRequestOperationType.FloorPlanCreate]: ChangeRequestDomain.FLOOR_PLAN,
  [ChangeRequestOperationType.FloorPlanUpdate]: ChangeRequestDomain.FLOOR_PLAN,
  [ChangeRequestOperationType.FloorPlanDelete]: ChangeRequestDomain.FLOOR_PLAN,
  [ChangeRequestOperationType.LayoutCreate]: ChangeRequestDomain.LAYOUT,
  [ChangeRequestOperationType.LayoutUpdate]: ChangeRequestDomain.LAYOUT,
  [ChangeRequestOperationType.LayoutDelete]: ChangeRequestDomain.LAYOUT,
  [ChangeRequestOperationType.ProductPageCreate]:
    ChangeRequestDomain.PRODUCT_PAGE,
  [ChangeRequestOperationType.ProductPageUpdate]:
    ChangeRequestDomain.PRODUCT_PAGE,
  [ChangeRequestOperationType.ProductPageAddItems]:
    ChangeRequestDomain.PRODUCT_PAGE,
  [ChangeRequestOperationType.ProductPageDelete]:
    ChangeRequestDomain.PRODUCT_PAGE,
  [ChangeRequestOperationType.StoreSettingsUpdate]:
    ChangeRequestDomain.STORE_SETTINGS,
  [ChangeRequestOperationType.OnlineOrderSettingsUpdate]:
    ChangeRequestDomain.ONLINE_ORDER_SETTINGS,
  [ChangeRequestOperationType.DeviceConfigCreate]:
    ChangeRequestDomain.DEVICE_CONFIG,
  [ChangeRequestOperationType.DeviceConfigUpdate]:
    ChangeRequestDomain.DEVICE_CONFIG,
  [ChangeRequestOperationType.DeviceConfigDelete]:
    ChangeRequestDomain.DEVICE_CONFIG,
  [ChangeRequestOperationType.PaymentMethodCreate]:
    ChangeRequestDomain.PAYMENT_METHOD,
  [ChangeRequestOperationType.PaymentMethodUpdate]:
    ChangeRequestDomain.PAYMENT_METHOD,
  [ChangeRequestOperationType.PaymentMethodDelete]:
    ChangeRequestDomain.PAYMENT_METHOD,
  [ChangeRequestOperationType.PrinterCreate]: ChangeRequestDomain.PRINTER,
  [ChangeRequestOperationType.PrinterUpdate]: ChangeRequestDomain.PRINTER,
  [ChangeRequestOperationType.PrinterDelete]: ChangeRequestDomain.PRINTER,
  [ChangeRequestOperationType.CourseCreate]: ChangeRequestDomain.COURSE,
  [ChangeRequestOperationType.CourseUpdate]: ChangeRequestDomain.COURSE,
  [ChangeRequestOperationType.CourseDelete]: ChangeRequestDomain.COURSE,
  [ChangeRequestOperationType.OfferCreate]: ChangeRequestDomain.OFFER,
  [ChangeRequestOperationType.OfferUpdate]: ChangeRequestDomain.OFFER,
  [ChangeRequestOperationType.OfferDelete]: ChangeRequestDomain.OFFER,
  [ChangeRequestOperationType.SupplierCreate]: ChangeRequestDomain.SUPPLIER,
  [ChangeRequestOperationType.SupplierUpdate]: ChangeRequestDomain.SUPPLIER,
  [ChangeRequestOperationType.SupplierDelete]: ChangeRequestDomain.SUPPLIER,
};

export const CHANGE_REQUEST_SINGLETON_CONTEXT_ID = "current";

export const CHANGE_REQUEST_DOMAIN_TARGET_KEY: Record<
  ChangeRequestDomain,
  string | null
> = {
  [ChangeRequestDomain.PRODUCT]: "productId",
  [ChangeRequestDomain.GROUP]: "groupId",
  [ChangeRequestDomain.MODIFIER]: "modifierId",
  [ChangeRequestDomain.MODIFIER_GROUP]: "modifierGroupId",
  [ChangeRequestDomain.UNIT]: "unitId",
  [ChangeRequestDomain.PREP_STATION]: "prepStationId",
  [ChangeRequestDomain.BOOKKEEPING_ACCOUNT]: "bookkeepingAccountId",
  [ChangeRequestDomain.FLOOR_PLAN]: "floorPlanId",
  [ChangeRequestDomain.LAYOUT]: "layoutId",
  [ChangeRequestDomain.PRODUCT_PAGE]: "productPageId",
  [ChangeRequestDomain.STORE_SETTINGS]: null,
  [ChangeRequestDomain.ONLINE_ORDER_SETTINGS]: null,
  [ChangeRequestDomain.DEVICE_CONFIG]: "deviceConfigId",
  [ChangeRequestDomain.PAYMENT_METHOD]: "paymentMethodId",
  [ChangeRequestDomain.PRINTER]: "printerId",
  [ChangeRequestDomain.COURSE]: "courseId",
  [ChangeRequestDomain.OFFER]: "offerId",
  [ChangeRequestDomain.SUPPLIER]: "supplierId",
};

export const CHANGE_REQUEST_REFERENCE_FIELDS: Readonly<
  Record<string, ChangeRequestDomain>
> = {
  productId: ChangeRequestDomain.PRODUCT,
  groupId: ChangeRequestDomain.GROUP,
  productGroupId: ChangeRequestDomain.GROUP,
  modifierId: ChangeRequestDomain.MODIFIER,
  modifiers: ChangeRequestDomain.MODIFIER,
  alwaysAppliedModifiers: ChangeRequestDomain.MODIFIER,
  modifierGroupId: ChangeRequestDomain.MODIFIER_GROUP,
  modifierGroups: ChangeRequestDomain.MODIFIER_GROUP,
  unitId: ChangeRequestDomain.UNIT,
  prepStationId: ChangeRequestDomain.PREP_STATION,
  bookkeepingAccountId: ChangeRequestDomain.BOOKKEEPING_ACCOUNT,
  takeawayBookkeepingAccountId: ChangeRequestDomain.BOOKKEEPING_ACCOUNT,
  floorPlanId: ChangeRequestDomain.FLOOR_PLAN,
  layoutId: ChangeRequestDomain.LAYOUT,
  productPageId: ChangeRequestDomain.PRODUCT_PAGE,
  defaultProductPageId: ChangeRequestDomain.PRODUCT_PAGE,
  productIds: ChangeRequestDomain.PRODUCT,
  groupIds: ChangeRequestDomain.GROUP,
  deviceConfigId: ChangeRequestDomain.DEVICE_CONFIG,
  paymentMethodId: ChangeRequestDomain.PAYMENT_METHOD,
  paymentMethodsOrder: ChangeRequestDomain.PAYMENT_METHOD,
  printerId: ChangeRequestDomain.PRINTER,
  printerIds: ChangeRequestDomain.PRINTER,
  defaultReceiptPrinter: ChangeRequestDomain.PRINTER,
  serviceOrderPrinterId: ChangeRequestDomain.PRINTER,
  defaultLabelPrinterId: ChangeRequestDomain.PRINTER,
  courseId: ChangeRequestDomain.COURSE,
  offerId: ChangeRequestDomain.OFFER,
  supplierId: ChangeRequestDomain.SUPPLIER,
};

export const getChangeRequestOperationKind = (
  type: ChangeRequestOperationType,
): ChangeRequestOperationKind => {
  if (type.endsWith("_create")) {
    return "create";
  }
  if (type.endsWith("_delete")) {
    return "delete";
  }
  return "update";
};

export const getChangeRequestOperationTypesForDomains = (
  domains: readonly ChangeRequestDomain[],
): ChangeRequestOperationType[] =>
  Object.values(ChangeRequestOperationType).filter((type) =>
    domains.includes(CHANGE_REQUEST_OPERATION_DOMAIN[type]),
  );

export const CHANGE_REQUEST_DOMAIN_PERMISSION: Record<
  ChangeRequestDomain,
  Permissions
> = {
  [ChangeRequestDomain.PRODUCT]: Permissions.PRODUCT_MANAGEMENT,
  [ChangeRequestDomain.GROUP]: Permissions.PRODUCT_MANAGEMENT,
  [ChangeRequestDomain.MODIFIER]: Permissions.PRODUCT_MANAGEMENT,
  [ChangeRequestDomain.MODIFIER_GROUP]: Permissions.PRODUCT_MANAGEMENT,
  [ChangeRequestDomain.UNIT]: Permissions.PRODUCT_MANAGEMENT,
  [ChangeRequestDomain.SUPPLIER]: Permissions.PRODUCT_MANAGEMENT,
  [ChangeRequestDomain.BOOKKEEPING_ACCOUNT]: Permissions.STORE_SETTINGS,
  [ChangeRequestDomain.PREP_STATION]: Permissions.RESTAURANT_SETUP,
  [ChangeRequestDomain.COURSE]: Permissions.RESTAURANT_SETUP,
  [ChangeRequestDomain.FLOOR_PLAN]: Permissions.MANAGE_FLOOR_PLANS,
  [ChangeRequestDomain.LAYOUT]: Permissions.MANAGE_LAYOUTS,
  [ChangeRequestDomain.PRODUCT_PAGE]: Permissions.MANAGE_PRODUCT_PAGES,
  [ChangeRequestDomain.STORE_SETTINGS]: Permissions.STORE_SETTINGS,
  [ChangeRequestDomain.ONLINE_ORDER_SETTINGS]: Permissions.MANAGE_ONLINE_ORDERS,
  [ChangeRequestDomain.DEVICE_CONFIG]: Permissions.DEVICE_CONFIGURATION,
  [ChangeRequestDomain.PAYMENT_METHOD]: Permissions.PAYMENT_METHOD_MANAGEMENT,
  [ChangeRequestDomain.PRINTER]: Permissions.PRINTERS_MANAGEMENT,
  [ChangeRequestDomain.OFFER]: Permissions.MANAGE_OFFERS,
};

export const getChangeRequestRequiredPermissions = (
  operationTypes: readonly ChangeRequestOperationType[],
): Permissions[] => [
  ...new Set(
    operationTypes.map(
      (type) =>
        CHANGE_REQUEST_DOMAIN_PERMISSION[CHANGE_REQUEST_OPERATION_DOMAIN[type]],
    ),
  ),
];

export const isChangeRequestApprovable = (status: ChangeRequestStatus) =>
  status === ChangeRequestStatus.DRAFT ||
  status === ChangeRequestStatus.PARTIALLY_APPLIED;
